"use client";
import AccordionText from "@/components/AccordionTextRefresh";
import AccordionTextItem from "@/components/AccordionTextRefresh/AccordionTextItemRefresh";
import type { ProcessedCourse } from "@/common/course/types";
import type { CourseRun } from "@/common/courseRun/types";
import type { Instructor } from "@/common/instructor/types";
import InstructorList from "@/components/InstructorList";
import LanguageOverview from "../ContentRefresh/LanguageOverview";
import CertificateOption from "./CertificateOption";
import useTranslations from "@/i18n/useTranslations";

export interface CourseDetailsProps {
  formattedLanguage?: string | null;
  formattedTranscripts?: string[];
  formattedAiLanguages?: string[];
  courseRunKey?: string;
  category?: string;
  courseSlug?: string;
  instructors?: Omit<Instructor, "aboutMe">[];
  course?: ProcessedCourse;
  courseRun?: CourseRun;
  partnerName?: string;
}

function getAuditCourseUrl(courseRunKey: string | undefined) {
  return courseRunKey
    ? `https://learning.edx.org/course/${courseRunKey}/home?audit_mode=`
    : "#";
}

export default function CourseDetails({
  formattedLanguage,
  formattedTranscripts = [],
  formattedAiLanguages = [],
  courseRunKey,
  instructors = [],
  course,
  courseRun,
  partnerName,
}: CourseDetailsProps) {
  const t = useTranslations("Course");
  const auditCourseUrl = getAuditCourseUrl(courseRunKey);

  return (
    <div>
      <h2 className="font-bold md:text-3xl mt-0 mb-4">{t("courseDetails")}</h2>

      <div className="flex flex-col gap-4">
        <AccordionText>
          <AccordionTextItem trigger={t("languageTranslationsPrerequisites")}>
            <div className="py-2">
              <LanguageOverview
                formattedLanguage={formattedLanguage}
                formattedTranscripts={formattedTranscripts}
                formattedAiLanguages={formattedAiLanguages}
                prerequisitesHtml={course?.prerequisitesHtml ?? ""}
              />
            </div>
          </AccordionTextItem>
        </AccordionText>

        {course && courseRun && partnerName && (
          <AccordionText>
            <AccordionTextItem trigger={t("certificateOptions")}>
              <CertificateOption
                auditCourseUrl={auditCourseUrl}
                partnerName={partnerName}
                course={course}
                courseRun={courseRun}
              />
            </AccordionTextItem>
          </AccordionText>
        )}

        <AccordionText>
          <AccordionTextItem trigger={t("instructors")}>
            <InstructorList instructors={instructors} variant="panel" />
          </AccordionTextItem>
        </AccordionText>
      </div>
    </div>
  );
}
